import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, DataTable } from "../components/Table";
import { StatusBadge } from "../components/StatusBadge";
import { MagnifyingGlass, Trash, PencilSimple, X } from "@phosphor-icons/react";
import { DeviceFormSections, DEVICE_DEFAULTS } from "../components/DeviceFormSections";

const MANAGER_ROLES = ["super_admin", "oem_admin", "state_admin", "distributor", "dealer", "fleet_admin"];

export default function Vehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [groups, setGroups] = useState([]);
  const [q, setQ] = useState("");
  const [hidden, setHidden] = useState([]);
  const [edit, setEdit] = useState(null); // { id, imei, vehicle_number, ...fields }
  const [busy, setBusy] = useState(false);
  const timers = useRef({});
  const { user } = useAuth();
  const nav = useNavigate();

  const load = () => api.get("/vehicles").then((r) => setVehicles(r.data.vehicles));
  useEffect(() => {
    load();
    api.get("/groups").then((r) => setGroups(r.data.groups || [])).catch(() => {});
    return () => Object.values(timers.current).forEach(clearTimeout);
  }, []);

  const canManage = MANAGER_ROLES.includes(user?.role);

  const undo = (id) => {
    clearTimeout(timers.current[id]); delete timers.current[id];
    setHidden((h) => h.filter((x) => x !== id));
    toast.success("Vehicle restored");
  };

  const removeVehicle = (v) => {
    setHidden((h) => [...h, v.id]);
    timers.current[v.id] = setTimeout(async () => {
      delete timers.current[v.id];
      try { await api.delete(`/vehicles/${v.id}`); load(); }
      catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); setHidden((h) => h.filter((x) => x !== v.id)); }
    }, 6000);
    toast(`${v.vehicle_number} removed`, {
      description: "Device & telemetry will be deleted.",
      duration: 6000,
      action: { label: "Undo", onClick: () => undo(v.id) },
    });
  };

  const openEdit = async (v) => {
    try {
      const r = await api.get(`/vehicles/${v.id}`);
      const d = r.data;
      const grp = groups.find((g) => (g.vehicle_ids || []).includes(v.id));
      setEdit({
        id: d.id, imei: d.imei, vehicle_number: d.vehicle_number,
        device_model: d.device_model || DEVICE_DEFAULTS.device_model,
        vehicle_type: d.vehicle_type || DEVICE_DEFAULTS.vehicle_type,
        vehicle_group_id: grp?.id || "",
        sim_number: d.sim_number || "", sim2: d.sim2 || "",
        driver_number: d.driver_mobile || "", operator: d.operator || "Airtel",
        initial_state: d.activation_status === "NEW" ? "inactive" : "active",
        license_expiry: d.license_expiry || "", license_type: d.license_type || DEVICE_DEFAULTS.license_type,
        validity: d.validity || DEVICE_DEFAULTS.validity,
        speed_limit: d.overspeed_limit ?? DEVICE_DEFAULTS.speed_limit,
        opening_odometer: d.opening_odometer ?? 0, idle_threshold: d.idle_threshold ?? 5,
        ignition_connected: d.ignition_connected ?? true, parking_mode: d.parking_mode ?? false,
        overspeed_alerts: d.overspeed_alerts ?? true, disable_login: d.disable_login ?? false,
        remarks: d.remarks || "",
        enable_data_push: d.enable_data_push ?? false, gov_vltd_push: d.gov_vltd_push ?? false,
        customer_webhook: d.customer_webhook ?? false,
      });
    } catch (e) { toast.error("Could not load device"); }
  };

  const setE = (k, v) => setEdit((s) => ({ ...s, [k]: v }));

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.put(`/vehicles/${edit.id}/settings`, edit);
      toast.success(`${edit.vehicle_number} settings updated`);
      setEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };

  const filtered = vehicles.filter((v) => !hidden.includes(v.id) &&
    (!q || v.vehicle_number.toLowerCase().includes(q.toLowerCase()) || v.imei.includes(q)));

  const columns = [
    { key: "vehicle_number", header: "Vehicle", render: (r) => <span className="font-semibold">{r.vehicle_number}</span> },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { key: "device_model", header: "Device" },
    { key: "imei", header: "IMEI", render: (r) => <span className="font-mono text-xs">{r.imei}</span> },
    { key: "driver_name", header: "Driver" },
    { key: "operator", header: "SIM" },
    { key: "speed", header: "Speed", render: (r) => `${r.speed} km/h` },
    { key: "today_distance", header: "Today", render: (r) => `${r.today_distance} km` },
    { key: "health_score", header: "Health", render: (r) => <span className="font-mono">{r.health_score}</span> },
  ];
  if (canManage) {
    columns.push({ key: "actions", header: "", render: (r) => (
      <span className="flex items-center gap-1">
        <button data-testid={`edit-vehicle-${r.vehicle_number}`} onClick={(e) => { e.stopPropagation(); openEdit(r); }}
          className="text-muted-foreground hover:text-primary p-1" title="Edit device settings"><PencilSimple size={16} weight="bold" /></button>
        <button data-testid={`delete-vehicle-${r.vehicle_number}`} onClick={(e) => { e.stopPropagation(); removeVehicle(r); }}
          className="text-muted-foreground hover:text-destructive p-1" title="Remove vehicle & device"><Trash size={16} weight="bold" /></button>
      </span>
    )});
  }

  return (
    <div className="p-5 space-y-4">
      <PageHeader title="Vehicles" subtitle={`${filtered.length} vehicles in fleet`}
        actions={
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2 top-2.5 text-muted-foreground" />
            <input data-testid="vehicles-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search vehicle / IMEI"
              className="bg-card border border-border rounded-sm pl-7 pr-3 py-1.5 text-sm w-64 focus:ring-1 focus:ring-primary focus:outline-none" />
          </div>
        } />
      <DataTable testid="vehicles-table" columns={columns} rows={filtered} onRowClick={() => nav("/tracking")}
        empty="No vehicles yet — add one from Add Device" />

      {edit && (
        <div className="fixed inset-0 z-[3000] bg-black/60 flex items-center justify-center p-4" data-testid="edit-device-modal">
          <div className="bg-white rounded-md w-full max-w-4xl max-h-[92vh] overflow-y-auto shadow-2xl">
            <div className="px-6 py-4 bg-gradient-to-r from-[#2f4bff] to-[#4361ee] text-white flex items-start justify-between sticky top-0 z-10">
              <div>
                <div className="font-display font-black text-xl">Edit device — {edit.vehicle_number}</div>
                <div className="text-sm text-white/80">IMEI <span className="font-mono">{edit.imei}</span> · update SIM, license and operating settings.</div>
              </div>
              <button data-testid="edit-device-close" onClick={() => setEdit(null)} className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center"><X size={16} /></button>
            </div>
            <div className="p-6 space-y-4">
              <DeviceFormSections f={edit} set={setE} groups={groups} />
            </div>
            <div className="px-6 py-3 border-t border-border flex items-center justify-end gap-2 sticky bottom-0 bg-white">
              <button onClick={() => setEdit(null)} className="px-5 py-2.5 text-sm font-semibold border border-border rounded-md hover:bg-black/5">Cancel</button>
              <button data-testid="edit-device-save" disabled={busy} onClick={saveEdit} className="px-6 py-2.5 text-sm font-bold bg-primary text-white rounded-md disabled:opacity-50 hover:opacity-90">{busy ? "Saving…" : "Save changes"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
